<template>
    <div v-if="missedTransactionQuery" class="w-full h-full">
        <FormModal v-if="showFilters" @cancel="showFilters = false; offerType = []; retailerCategory = undefined"
            @confirm="showFilters = false; addSupportTicketAction()" :valid="true" title="Add action"
            subtitle="Add an action to resolve this query" submitButtonText="Submit action">
            <div class="grid gap-3">
                <CustomSelect v-model="supportAction.actions" :pre-select="orderBy" :options="orderByList"
                    label="Select action" />
                <CustomInput v-model="supportAction.notes" type="text" multiline label="Notes" />
            </div>
        </FormModal>

        <SubHeader :title="`Ticket ID: ${missedTransactionQuery.id} `"
            :subText="`Resolved: ${missedTransactionQuery.resolved} `">
            <template v-slot:buttons>
                <CustomButton id="createContract" :text="missedTransactionQuery.resolved ? 'Completed' : 'Add action'"
                    icon="Plus" :disabled="missedTransactionQuery.resolved"
                    :type="missedTransactionQuery.resolved ? 'confirm' : 'action'" @click='showFilters = true' />
            </template>
        </SubHeader>

        <WhiteBox title="Support ticket" subText="View the support ticket">
            <template v-slot:content>
                <div class="my-2 bg-gray-100 p-2 border-2">
                    <p class="text-sm font-bold">Message:</p>
                    <p class="text-md">{{ missedTransactionQuery.endUserMessage }}</p>

                </div>
                <EasyDataTable :headers="ticketTable" :items="[missedTransactionQuery]" :loading="loading"
                    :hide-footer="true" :no-hover="true" :table-min-height="0">
                    <template #loading>
                        <div class="p-4 w-full flex justify-center">
                            <svg class="animate-spin text-gray-400 w-12 h-12" xmlns="http://www.w3.org/2000/svg"
                                fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                    stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                </path>
                            </svg>
                        </div>
                    </template>
                    <template #empty-message>
                        <div class="flex justify-center py-4">
                            <div
                                class="flex justify-center items-center text-gray-500 rounded-full bg-gray-50 px-5 py-2 ">
                                <p class="text-sm mr-2">No results</p>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                    stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                    <path stroke-linecap="round" stroke-linejoin="round"
                                        d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
                                </svg>
                            </div>
                        </div>
                    </template>
                    <template #header-action="header">
                        <div class="w-full text-right">
                            {{ header.text }}
                        </div>
                    </template>
                    <template #item-createdOn="ticket">
                        {{ niceDateTime(ticket.createdOn) }}
                    </template>

                    <template #item-updatedOn="ticket">
                        <div v-if="ticket.updatedOn != null">{{ niceDateTime(ticket.updatedOn) }}</div>
                    </template>
                    <template #item-resolvedOn="ticket">
                        <div v-if="ticket.resolvedOn != null">{{ niceDateTime(ticket.resolvedOn) }}</div>
                    </template>

                </EasyDataTable>

            </template>

        </WhiteBox>

        <WhiteBox title="Expected offer data" subText="View the expected offer">
            <template v-slot:content>
                <div>
                    <SubHeader :logo="expectedOffer.retailer.logo" :title="expectedOffer.retailer.retailerName"
                        :subText="`Offer Id: ${expectedOffer.id}`" icon="Store">
                    </SubHeader>

                    <p class="text-md font-medium my-2">Offer information</p>

                    <EasyDataTable :table-min-height="0" :headers="activatedOffersHeaders" :items="[expectedOffer]"
                        :loading="loading" :hide-footer="true" :no-hover="true">
                        <template #loading>
                            <div class="p-4 w-full flex justify-center">
                                <svg class="animate-spin text-gray-400 w-12 h-12" xmlns="http://www.w3.org/2000/svg"
                                    fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                        stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                    </path>
                                </svg>
                            </div>
                        </template>
                        <template #empty-message>
                            <div class="flex justify-center py-4">
                                <div
                                    class="flex justify-center items-center text-gray-500 rounded-full bg-gray-50 px-5 py-2 ">
                                    <p class="text-sm mr-2">No results</p>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                        stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                        <path stroke-linecap="round" stroke-linejoin="round"
                                            d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
                                    </svg>
                                </div>
                            </div>
                        </template>
                        <template #header-action="header">
                            <div class="w-full text-right">
                                {{ header.text }}
                            </div>
                        </template>
                        <template #item-action="retailer">
                            <div class="flex justify-end gap-x-2 w-full text-right">
                                <div v-if="retailer.offerApproval === 'APPROVED'">
                                    <CustomButton v-if="retailer.active" :requiresConfirmation="true"
                                        confrimationModalTitle="Deactivate this offer"
                                        confrimationModalMessage="Confirm you would like to deactivate this offer"
                                        confrimationModalButtonText="Deactivate"
                                        @confirmClick="deActivateOffer(retailer.id)" small text="Deactivate"
                                        type="warning" />
                                    <CustomButton v-else :requiresConfirmation="true"
                                        confrimationModalTitle="Active this offer"
                                        confrimationModalMessage="Confirm you would like to activate this offer"
                                        confrimationModalButtonText="Activate" small text="Activate" type="action"
                                        @confirmClick="activateOffer(retailer.id)" />
                                </div>
                                <CustomButton small text="Edit" type="action" @click="handleEditOffer()" />
                            </div>
                        </template>
                        <template #item-matchAgainst="retailers">
                            <p v-if="retailers.offer.validFor == 'ALL_CHANNELS'">
                                ALL_TRANSACTIONS

                            </p>
                            <p v-else>
                                {{ retailers.offer.validFor }}
                            </p>
                        </template>
                        <template #item-offertype="retailers">
                            <OfferTypePill :type="retailers.offer.offerType" />
                        </template>
                        <template #item-active="retailer">
                            <StatusPill v-if="retailer.active" text="Live" colour="green" />
                            <StatusPill v-else text="Deactivated" colour="yellow" />
                        </template>
                        <template #item-selectedCustomerTiers="">
                            <StatusPill :class="id > 0 ? 'ml-1' : ''" v-for="tier, id in selectedSegments" :key="id"
                                :text='tier' colour="gray" />
                        </template>

                        <template #item-cap="retailers">
                            <div v-if="retailers.offer.spendingCapped">
                                <StatusPill
                                    :text='getCurrencySymbol(this.offer.currency) + retailers.offer.spendingCapAmount'
                                    colour="gray" />
                            </div>
                        </template>
                        <template #item-offerApproval="retailer">
                            <StatusPill v-if="retailer.offerApproval === 'APPROVED'" text="Approved" colour="green" />
                            <StatusPill v-if="retailer.offerApproval === 'PENDING'" text="Pending approval"
                                colour="yellow" />
                            <StatusPill v-if="retailer.offerApproval === 'DECLINED'" text="Approval declined"
                                colour="red" />
                        </template>
                    </EasyDataTable>

                    <p class="text-md font-medium my-2">Cashback conditions</p>

                    <EasyDataTable :table-min-height="0" :headers="cashbackConditions"
                        :items="expectedOffer.cashbackEarnings" :loading="loading" :hide-footer="true" :no-hover="true">
                        <template #loading>
                            <div class="p-4 w-full flex justify-center">
                                <svg class="animate-spin text-gray-400 w-12 h-12" xmlns="http://www.w3.org/2000/svg"
                                    fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                        stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                    </path>
                                </svg>
                            </div>
                        </template>
                        <template #empty-message>
                            <div class="flex justify-center py-4">
                                <div
                                    class="flex justify-center items-center text-gray-500 rounded-full bg-gray-50 px-5 py-2 ">
                                    <p class="text-sm mr-2">No results</p>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                        stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                        <path stroke-linecap="round" stroke-linejoin="round"
                                            d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
                                    </svg>
                                </div>
                            </div>
                        </template>
                        <template #header-action="header">
                            <div class="w-full text-right">
                                {{ header.text }}
                            </div>
                        </template>
                        <template #item-usetype>
                            <StatusPill text="Multi-use" colour="blue" />
                        </template>
                        <template #item-customertype="customerType">
                            <StatusPill :text="customerType.customerType" colour="blue" />
                        </template>
                        <template #item-fixedDistributorCommission="val">
                            <div v-if="val.conditions.length === 1" class="">
                                <StatusPill v-if="val.conditions[0].fixedDistributorCommission" text="True"
                                    colour="green" />
                                <StatusPill v-else text="False" colour="red" />
                            </div>
                        </template>
                        <template #item-userCashback="val">
                            <CashbackPill v-if="val.conditions.length === 1"
                                :text="val.conditions[0].value.endUserValue == null ? 'Please set' : val.conditions[0].value.endUserValue.toFixed(2)"
                                :type="val.conditions[0].value.type"
                                :currency="getCurrencySymbol(this.expectedOffer.offer.currency)"
                                :max="val.conditions[0].value.maximum" />
                        </template>
                        <template #item-totalCashback="val">
                            <div v-if="val.conditions.length === 1">
                                <CashbackPill v-if="val.conditions[0].value.maxAvailableValue != null"
                                    :text="val.conditions[0].value.maxAvailableValue.toFixed(2)"
                                    :type="val.conditions[0].value.type"
                                    :currency="getCurrencySymbol(this.expectedOffer.offer.currency)"
                                    :max="val.conditions[0].value.maximum" />
                                <CashbackPill v-else text="Online offers get a split of commission" />
                            </div>

                        </template>
                        <template #item-publisherCashback="val">
                            <CashbackPill v-if="val.conditions.length === 1"
                                :text="val.conditions[0].value.clientValue == null ? 'Please set' : val.conditions[0].value.clientValue.toFixed(2)"
                                :type="val.conditions[0].value.type"
                                :currency="getCurrencySymbol(this.expectedOffer.offer.currency)"
                                :max="val.conditions[0].value.maximum" />
                        </template>
                        <template #item-cashbackType="val">
                            <StatusPill v-if="val.conditions.length === 1" :text="val.conditions[0].value.type"
                                colour="blue" />
                        </template>
                        <template #item-conditions="val">
                            <div v-if="val.conditions.length === 1" class="">
                                <div
                                    class="bg-blue-100 border border-blue-400 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full text-nowrap w-min">
                                    <span v-if="val.conditions[0].type === 'GREATER_THAN'">
                                        Greater than {{ getCurrencySymbol(this.expectedOffer.offer.currency) }}
                                    </span>
                                    <span v-if="val.conditions[0].type === 'LESS_THAN'">
                                        Less than {{ getCurrencySymbol(this.expectedOffer.offer.currency) }}
                                    </span>
                                    <span v-if="val.conditions[0].type === 'BETWEEN'">
                                        Between {{ getCurrencySymbol(this.expectedOffer.offer.currency) }}
                                    </span>
                                    {{ val.conditions[0].lowerBound }}
                                    <span v-if="val.conditions[0].type === 'BETWEEN'">
                                        and {{ getCurrencySymbol(this.expectedOffer.offer.currency) }}
                                    </span>
                                    {{ val.conditions[0].upperBound }}
                                </div>
                            </div>
                        </template>
                    </EasyDataTable>


                    <p class="text-md font-medium my-2">Avaliable channels</p>


                    <EasyDataTable :table-min-height="0" :headers="channelsTable" :items="expectedOffer.offer.channels"
                        :loading="loading" :hide-footer="true" :no-hover="true">
                        <template #loading>
                            <div class="p-4 w-full flex justify-center">
                                <svg class="animate-spin text-gray-400 w-12 h-12" xmlns="http://www.w3.org/2000/svg"
                                    fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                        stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                    </path>
                                </svg>
                            </div>
                        </template>
                        <template #empty-message>
                            <div class="flex justify-center py-4">
                                <div
                                    class="flex justify-center items-center text-gray-500 rounded-full bg-gray-50 px-5 py-2 ">
                                    <p class="text-sm mr-2">No results</p>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                        stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                        <path stroke-linecap="round" stroke-linejoin="round"
                                            d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
                                    </svg>
                                </div>
                            </div>
                        </template>
                        <template #header-action="header">
                            <div class="w-full text-right">
                                {{ header.text }}
                            </div>
                        </template>
                    </EasyDataTable>






                </div>
            </template>
        </WhiteBox>

        <div class="grid grid-cols-12 gap-4">
            <div class="col-span-full xl:col-span-4">
                <WhiteBox title="Data" subText="Select the data you want to view">
                    <template v-slot:content>
                        <CustomButton @click="selectedDataItem = 'TRANSACTION_DATA'" type="default" class="my-2"
                            text="Transaction Data"></CustomButton>
                        <CustomButton @click="selectedDataItem = 'CASHBACK_DATA'" type="default" class="my-2"
                            text="Cashback Data "></CustomButton>
                        <CustomButton @click="selectedDataItem = 'END_USER_DATA'" type="default" class="my-2"
                            text="End User Data"></CustomButton>
                        <CustomButton @click="selectedDataItem = 'OFFER_ACTIVATIONS'" type="default" class="my-2"
                            text="Relevant Offer activations "></CustomButton>


                    </template>
                </WhiteBox>
            </div>

            <div class="col-span-full xl:col-span-8">
                <WhiteBox v-if="selectedDataItem == 'TRANSACTION_DATA'" title="Transaction Data"
                    subText="View the transaction">
                    <template v-slot:content>
                        <EasyDataTable :table-min-height="0" :headers="transactionDataHeaders" :items="transactionData"
                            :loading="loading" :hide-footer="true" :no-hover="true">
                            <template #loading>
                                <div class="p-4 w-full flex justify-center">
                                    <svg class="animate-spin text-gray-400 w-12 h-12" xmlns="http://www.w3.org/2000/svg"
                                        fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                            stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                        </path>
                                    </svg>
                                </div>
                            </template>
                            <template #item-id="transaction">
                                <div class="w-20 py-1">
                                    <p style="word-break:break-all; font-size:11px;">
                                        {{ transaction.id }}
                                    </p>
                                </div>
                            </template>
                            <template #item-bookingDateTime="transaction">
                                <div style="font-size:11px;" class="w-24 py-1">
                                    {{ niceDateTime(transaction.bookingDateTime) }}
                                </div>
                            </template>
                            <template #item-amount="transaction">
                                <CashbackPill
                                    :text="(Math.round(transaction.transactionAmount.amount * 100) / 100).toFixed(2)"
                                    type="FIXED" :currency="transaction.transactionAmount.currency" />
                            </template>
                            <template #item-retailer="transaction">
                                <div class="py-1 w-40">
                                    <EnrichedMerchant v-if="transaction.enrichedCreditorData != null"
                                        :logo="transaction.enrichedCreditorData.logo"
                                        :name="transaction.enrichedCreditorData.merchant"
                                        :website="transaction.enrichedCreditorData.website"
                                        :labels="transaction.enrichedCreditorData.labels == null ? '' : transaction.enrichedCreditorData.labels[0]" />
                                </div>
                            </template>
                            <template #item-customerTransactionId="transaction">
                                <div class="w-28 py-1">
                                    <p style="font-size:11px; word-break:break-all;">
                                        {{ transaction.internalTransactionId }}
                                    </p>
                                </div>
                            </template>
                            <template #item-remittanceInformationUnstructured="transaction">
                                <div class="w-64 py-1">
                                    <p style="font-size:11px;">
                                        {{ transaction.remittanceInformationUnstructured }}
                                    </p>
                                </div>
                            </template>
                            <template #item-internalEndUserId="transaction">
                                <div class="w-28 py-1">
                                    <p style="word-break:break-all; font-size:11px;">
                                        {{ transaction.endUserId }}
                                    </p>
                                </div>
                            </template>
                            <template #item-action="transaction">
                                <div class="w-28 py-1">
                                    <div class="m-2" v-if="showDeleteTransactionButton">
                                        <CustomButton :requiresConfirmation="true"
                                            confrimationModalTitle="Delete this transaction"
                                            confrimationModalMessage="Confirm you would like to delete this transaction"
                                            confrimationModalButtonText="Delete" small text="Delete" type="warning"
                                            @confirmClick="deleteTransactionFromId(transaction.id)" />
                                    </div>
                                </div>
                            </template>

                            <template #item-offerIds="transaction">
                                <div>
                                    <div v-for="id, index in transaction.offersAvailable.map(val => val.id)"
                                        :key="index">
                                        <StatusPill :text="id" colour="gray" />

                                    </div>

                                </div>
                            </template>



                            <template #empty-message>
                                <div class="flex justify-center py-4">
                                    <div
                                        class="flex justify-center items-center text-gray-500 rounded-full bg-gray-50 px-5 py-2 ">
                                        <p class="text-sm mr-2">No results</p>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                            stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
                                        </svg>
                                    </div>
                                </div>
                            </template>
                        </EasyDataTable>
                    </template>
                </WhiteBox>

                <WhiteBox v-if="selectedDataItem == 'END_USER_DATA'" title="End user data"
                    subText="View the end user's info">
                    <template v-slot:content>
                        <div>
                            <SubHeader :title="`End user Id: ${endUser.id}`" icon="User">
                            </SubHeader>

                            <p class="text-md font-medium my-2">Set tiers</p>

                            <EasyDataTable :table-min-height="0" :headers="tiersHeader" :items="listOfTiers"
                                :loading="loading" :hide-footer="true" :no-hover="true">
                                <template #loading>
                                    <div class="p-4 w-full flex justify-center">
                                        <svg class="animate-spin text-gray-400 w-12 h-12"
                                            xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                                stroke-width="4"></circle>
                                            <path class="opacity-75" fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                            </path>
                                        </svg>
                                    </div>
                                </template>
                                <template #empty-message>
                                    <div class="flex justify-center py-4">
                                        <div
                                            class="flex justify-center items-center text-gray-500 rounded-full bg-gray-50 px-5 py-2 ">
                                            <p class="text-sm mr-2">No results</p>
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                                stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                                <path stroke-linecap="round" stroke-linejoin="round"
                                                    d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
                                            </svg>
                                        </div>
                                    </div>
                                </template>
                                <template #header-action="header">
                                    <div class="w-full text-right">
                                        {{ header.text }}
                                    </div>
                                </template>
                                <template #item-id="{ id }">
                                    {{ id }}
                                </template>
                            </EasyDataTable>

                            <p class="text-md font-medium my-2">Set offers</p>

                            <EasyDataTable :table-min-height="0" :headers="offerHeader" :items="listOfAssignedOffers"
                                :loading="loading" :hide-footer="true" :no-hover="true">
                                <template #loading>
                                    <div class="p-4 w-full flex justify-center">
                                        <svg class="animate-spin text-gray-400 w-12 h-12"
                                            xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                                stroke-width="4"></circle>
                                            <path class="opacity-75" fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                            </path>
                                        </svg>
                                    </div>
                                </template>
                                <template #empty-message>
                                    <div class="flex justify-center py-4">
                                        <div
                                            class="flex justify-center items-center text-gray-500 rounded-full bg-gray-50 px-5 py-2 ">
                                            <p class="text-sm mr-2">No results</p>
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                                stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                                <path stroke-linecap="round" stroke-linejoin="round"
                                                    d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
                                            </svg>
                                        </div>
                                    </div>
                                </template>
                                <template #header-action="header">
                                    <div class="w-full text-right">
                                        {{ header.text }}
                                    </div>
                                </template>
                            </EasyDataTable>
                        </div>
                    </template>
                </WhiteBox>

                <WhiteBox v-if="selectedDataItem == 'CASHBACK_DATA'" title="Cashback objects"
                    subText="View relevant cashback objects">
                    <template v-slot:content>
                        <p class="text-md font-md my-2">Cashback objects generated for this transaction</p>
                        <EasyDataTable :table-min-height="0" :headers="cashbackTable"
                            :items="cashbackObjectList.filter(object => object.transactionData.internalTransactionId == this.missedTransactionQuery.transactionId)"
                            :loading="loading" :hide-footer="true" :no-hover="true">
                            <template #loading>
                                <div class="p-4 w-full flex justify-center">
                                    <svg class="animate-spin text-gray-400 w-12 h-12" xmlns="http://www.w3.org/2000/svg"
                                        fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                            stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                        </path>
                                    </svg>
                                </div>
                            </template>
                            <template #empty-message>
                                <div class="flex justify-center py-4">
                                    <div
                                        class="flex justify-center items-center text-gray-500 rounded-full bg-gray-50 px-5 py-2 ">
                                        <p class="text-sm mr-2">No results</p>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                            stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
                                        </svg>
                                    </div>
                                </div>
                            </template>
                            <template #item-id="id">
                                <div class="w-20 py-1">
                                    <p style="word-break:break-all; font-size:11px;">{{ id.id }}</p>
                                </div>
                            </template>
                            <template #item-createdOn="createdOn">
                                <div class="w-20 py-1" style="font-size:11px;">
                                    {{ niceDateTime(createdOn.createdOn) }}
                                </div>
                            </template>
                            <template #item-status="status">
                                <div class="flex items-center">
                                    <div>
                                        <StatusPill v-if="status.status === 'PAID'" :text="status.status"
                                            colour="green" />
                                        <StatusPill v-else-if="status.status === 'DECLINED'" :text="status.status"
                                            colour="red" />
                                        <StatusPill v-else :text="status.status" colour="yellow" />
                                    </div>

                                    <div class="m-2" v-if="showTestCashbackButton">
                                        <p class="text-xs font-medium text-nowrap text-blue-600 underline hover:font-semibold cursor-pointer"
                                            @click="showManualCashbackUpdate = true; selectedCashback = status.id">
                                            Update status
                                        </p>
                                    </div>
                                </div>

                            </template>
                            <template #item-internalEndUserId="internalEndUserId">
                                <div class="w-20 py-1">
                                    <p style="word-break:break-all; font-size:11px;">{{ internalEndUserId.endUserId }}
                                    </p>
                                </div>
                            </template>
                            <template #item-retailer="cashback">
                                <div class="w-24 py-1">
                                    <LogoAndText v-if="cashback.programOffer"
                                        :logo="cashback.programOffer.retailer.logo"
                                        :text="cashback.programOffer.retailer.retailerName" />
                                </div>
                            </template>
                            <template #item-transactionValue="cashback">
                                <CashbackPill v-if="cashback.transactionData.transactionAmount != null"
                                    :text="(Math.round(cashback.transactionData.transactionAmount.amount * 100) / 100).toFixed(2)"
                                    type="FIXED" :currency="cashback.transactionData.transactionAmount.currency" />
                                <CashbackPill v-else text="Affiliate Transaction" type="FIXED" />
                            </template>
                            <template #item-userReceivable="cashback">
                                <CashbackPill
                                    :text="(Math.round(cashback.payoutData.endUserCashbackReceivable * 100) / 100).toFixed(2)"
                                    type="FIXED" :currency="cashback.payoutData.currency" />
                            </template>
                            <template #item-publisherReceivable="cashback">
                                <CashbackPill
                                    :text="(Math.round(cashback.payoutData.publisherCashbackReceivable * 100) / 100).toFixed(2)"
                                    type="FIXED" :currency="cashback.payoutData.currency" />
                            </template>
                            <template #item-declinedReason="cashback">
                                <StatusPill v-if="cashback.declinedReason" :text="cashback.declinedReason"
                                    colour="red" />
                            </template>
                        </EasyDataTable>

                        <p class="text-md font-md my-2">All cashback objects generated for this offer for this user</p>
                        <EasyDataTable :headers="cashbackTable" :items="cashbackObjectList" :loading="loading"
                            :hide-footer="true" :no-hover="true">
                            <template #loading>
                                <div class="p-4 w-full flex justify-center">
                                    <svg class="animate-spin text-gray-400 w-12 h-12" xmlns="http://www.w3.org/2000/svg"
                                        fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                            stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                        </path>
                                    </svg>
                                </div>
                            </template>
                            <template #empty-message>
                                <div class="flex justify-center py-4">
                                    <div
                                        class="flex justify-center items-center text-gray-500 rounded-full bg-gray-50 px-5 py-2 ">
                                        <p class="text-sm mr-2">No results</p>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                            stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
                                        </svg>
                                    </div>
                                </div>
                            </template>
                            <template #item-id="id">
                                <div class="w-20 py-1">
                                    <p style="word-break:break-all; font-size:11px;">{{ id.id }}</p>
                                </div>
                            </template>
                            <template #item-createdOn="createdOn">
                                <div class="w-20 py-1" style="font-size:11px;">
                                    {{ niceDateTime(createdOn.createdOn) }}
                                </div>
                            </template>
                            <template #item-status="status">
                                <div class="flex items-center">
                                    <div>
                                        <StatusPill v-if="status.status === 'PAID'" :text="status.status"
                                            colour="green" />
                                        <StatusPill v-else-if="status.status === 'DECLINED'" :text="status.status"
                                            colour="red" />
                                        <StatusPill v-else :text="status.status" colour="yellow" />
                                    </div>

                                    <div class="m-2" v-if="showTestCashbackButton">
                                        <p class="text-xs font-medium text-nowrap text-blue-600 underline hover:font-semibold cursor-pointer"
                                            @click="showManualCashbackUpdate = true; selectedCashback = status.id">
                                            Update status
                                        </p>
                                    </div>
                                </div>

                            </template>
                            <template #item-internalEndUserId="internalEndUserId">
                                <div class="w-20 py-1">
                                    <p style="word-break:break-all; font-size:11px;">{{ internalEndUserId.endUserId }}
                                    </p>
                                </div>
                            </template>
                            <template #item-retailer="cashback">
                                <div class="w-24 py-1">
                                    <LogoAndText v-if="cashback.programOffer"
                                        :logo="cashback.programOffer.retailer.logo"
                                        :text="cashback.programOffer.retailer.retailerName" />
                                </div>
                            </template>
                            <template #item-transactionValue="cashback">
                                <CashbackPill v-if="cashback.transactionData.transactionAmount != null"
                                    :text="(Math.round(cashback.transactionData.transactionAmount.amount * 100) / 100).toFixed(2)"
                                    type="FIXED" :currency="cashback.transactionData.transactionAmount.currency" />
                                <CashbackPill v-else text="Affiliate Transaction" type="FIXED" />
                            </template>
                            <template #item-userReceivable="cashback">
                                <CashbackPill
                                    :text="(Math.round(cashback.payoutData.endUserCashbackReceivable * 100) / 100).toFixed(2)"
                                    type="FIXED" :currency="cashback.payoutData.currency" />
                            </template>
                            <template #item-publisherReceivable="cashback">
                                <CashbackPill
                                    :text="(Math.round(cashback.payoutData.publisherCashbackReceivable * 100) / 100).toFixed(2)"
                                    type="FIXED" :currency="cashback.payoutData.currency" />
                            </template>
                            <template #item-declinedReason="cashback">
                                <StatusPill v-if="cashback.declinedReason" :text="cashback.declinedReason"
                                    colour="red" />
                            </template>
                        </EasyDataTable>

                    </template>
                </WhiteBox>

                <WhiteBox v-if="selectedDataItem == 'OFFER_ACTIVATIONS'" title="Offer activation data"
                    subText="View the activations for this offer">
                    <template v-slot:content>
                        <EasyDataTable :table-min-height="0" :headers="offerActivationHeaders"
                            :items="endUserActivations" :loading="loading" :hide-footer="true" :no-hover="true">
                            <template #loading>
                                <div class="p-4 w-full flex justify-center">
                                    <svg class="animate-spin text-gray-400 w-12 h-12" xmlns="http://www.w3.org/2000/svg"
                                        fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                            stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                        </path>
                                    </svg>
                                </div>
                            </template>
                            <template #item-id="transaction">
                                <div class="w-20 py-1">
                                    <p style="word-break:break-all; font-size:11px;">
                                        {{ transaction.id }}
                                    </p>
                                </div>
                            </template>
                            <template #item-activationDate="transaction">
                                <div style="font-size:11px;" class="w-24 py-1">
                                    {{ niceDateTime(transaction.activationDate) }}
                                </div>
                            </template>
                            <template #item-expiryDate="transaction">
                                <div style="font-size:11px;" class="w-24 py-1">
                                    {{ niceDateTime(transaction.expiryDate) }}
                                </div>
                            </template>
                            <template #item-currentlyActive="transaction">
                                <div style="font-size:11px;" class="w-24 py-1">
                                    {{ checkExpiry(transaction.expiryDate) }}
                                </div>
                            </template>

                            <template #empty-message>
                                <div class="flex justify-center py-4">
                                    <div
                                        class="flex justify-center items-center text-gray-500 rounded-full bg-gray-50 px-5 py-2 ">
                                        <p class="text-sm mr-2">No results</p>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                            stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
                                        </svg>
                                    </div>
                                </div>
                            </template>
                        </EasyDataTable>
                    </template>
                </WhiteBox>



            </div>


        </div>




        <WhiteBox title="Actions log" subText="View actions taken on this ticket">
            <template v-slot:buttons>
                <CustomButton small id="filterButton" text="Download logs" icon="Download" type="default"
                    @click="downloadLogs()" />
                <CustomButton small id="createContract"
                    :text="missedTransactionQuery.resolved ? 'Completed' : 'Add action'" icon="Plus"
                    :disabled="missedTransactionQuery.resolved"
                    :type="missedTransactionQuery.resolved ? 'confirm' : 'action'" @click='showFilters = true' />

            </template>

            <template v-slot:content>
                <EasyDataTable :headers="headersTable1" :items="actionLog" :loading="loading"
                    v-model:server-options="serverOptions" :server-items-length="serverItemsLength" :hide-footer="false"
                    :no-hover="true">
                    <template #loading>
                        <div class="p-4 w-full flex justify-center">
                            <svg class="animate-spin text-gray-400 w-12 h-12" xmlns="http://www.w3.org/2000/svg"
                                fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                    stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                </path>
                            </svg>
                        </div>
                    </template>
                    <template #empty-message>
                        <div class="flex justify-center py-4">
                            <div
                                class="flex justify-center items-center text-gray-500 rounded-full bg-gray-50 px-5 py-2 ">
                                <p class="text-sm mr-2">No results</p>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                    stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                    <path stroke-linecap="round" stroke-linejoin="round"
                                        d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
                                </svg>
                            </div>
                        </div>
                    </template>


                </EasyDataTable>
            </template>
        </WhiteBox>
    </div>
    <LoadingPlaceholder v-else
            :loading="true"
            :stats="true"
            :charts="true"
            :table="true"
        />
</template>

<script setup>
</script>

<script>
import WhiteBox from "@/components/WhiteBox.vue";
import CustomButton from "@/components/inputs/CustomButton.vue";
import LogoAndText from "@/components/LogoAndText.vue";
import OfferTypePill from "@/components/OfferTypePill.vue";
import FormModal from "@/components/modals/FormModal.vue";
import CustomSelect from "@/components/inputs/CustomSelect.vue";
import SubHeader from "@/components/SubHeader.vue";
import { DateTime } from 'luxon';
import StatusPill from "@/components/StatusPill.vue";
import CashbackPill from "@/components/CashbackPill.vue";
import EnrichedMerchant from "@/components/EnrichedMerchant.vue";
import CustomInput from "@/components/inputs/CustomInput.vue";
import LoadingPlaceholder from "@/components/LoadingPlaceholder.vue";


export default {
    name: "manage-publsihers",
    components: {
        WhiteBox,
        LoadingPlaceholder,
        CustomButton,
        CustomSelect,
        LogoAndText,
        OfferTypePill,
        FormModal,
        SubHeader,
        StatusPill,
        CashbackPill,
        EnrichedMerchant,
        CustomInput
    },
    props: {
    },

    data() {
        return {
            ticketId: this.$route.params.ticketId,
            missedTransactionQuery: undefined,
            expectedOffer: undefined,
            transactionData: [],
            cashbackObjectList: [],
            endUser: undefined,
            endUserActivations: [],
            actionLog: [],
            selectedDataItem: "TRANSACTION_DATA",



            serverOptions: {
                page: 1,
                rowsPerPage: 25,
            },
            serverItemsLength: undefined,
            showFilters: false,
            showErrorModal: false,
            showSuccessModal: false,
            showFormModal: false,
            ticket: [],


            selectedRetailer: undefined,
            loading: true,

            offerActivationHeaders: [
                { text: "Id", value: "id" },
                { text: "Activation Date", value: "activationDate" },
                { text: "Expiry Date", value: "expiryDate" },
                { text: "Currently Active", value: "currentlyActive" },
            ],

            cashbackTable: [
                { text: "Date created", value: "createdOn" },
                { text: "Id", value: "id" },
                { text: "Transaction Id", value: "transactionData.internalTransactionId" },
                { text: "User id", value: "internalEndUserId" },
                { text: "Status", value: "status" },
                { text: "Retailer", value: "retailer" },
                { text: "Transaction Value", value: "transactionValue" },
                { text: "End user receivable", value: "userReceivable" },
                { text: "Publisher receivable", value: "publisherReceivable" },
                { text: "Declined Reason", value: "declinedReason" },



            ],

            tiersHeader: [
                { text: "Tier id", value: "name" },
            ],
            offerHeader: [
                { text: "Offer id", value: "name" },
            ],

            transactionDataHeaders: [
                { text: "Transaction id", value: "customerTransactionId" },
                { text: "End user Id", value: "internalEndUserId" },
                { text: "Mid", value: "additionalCardData.mid" },
                { text: "Enriched data", value: "retailer" },
                { text: "Offer Matches", value: "offerIds" },
                { text: "Amount", value: "amount" },
                { text: "Input description", value: "remittanceInformationUnstructured" },
            ],

            cashbackConditions: [
                { text: "Total cashback", value: "totalCashback" },
                { text: "User cashback", value: "userCashback" },
                { text: "Your commission", value: "publisherCashback" },
                { text: "Customer type", value: "customerType" },
                { text: "Use Type", value: "usetype" },
                { text: "Cashback Type", value: "cashbackType" },
                { text: "Fixed split", value: "fixedDistributorCommission" },
                { text: "Spend condition", value: "conditions" },
            ],

            activatedOffersHeaders: [
                { text: "Offer type", value: "offertype" },
                { text: "Country", value: "offer.country" },
                { text: "Currency", value: "offer.currency" },
                { text: "Match against", value: "matchAgainst" },
                { text: "OB compatiable", value: "offer.obPublisherCompatible" },
                { text: "Offer Avaliable for", value: "customerTierType" },
                { text: "Spending cap", value: "cap" },
                { text: "Tiers selected", value: "selectedCustomerTiers" },
            ],

            ticketTable: [
                { text: "Ticket Id", value: "id" },
                { text: "User Id", value: "endUserId" },
                { text: "Transaction Id", value: "transactionId" },
                { text: "Expected offer id", value: "programOfferRequested.id" },
                { text: "Support level", value: "supportLevel" },
                { text: "Resolved", value: "resolved" },
                { text: "Created on", value: "createdOn" },
                { text: "Updated on", value: "updatedOn" },
                { text: "Resolved on", value: "resolvedOn" },
            ],

            channelsTable: [
                { text: "Channel name", value: "channelName" },
                { text: "Address", value: "address" },
                { text: "City", value: "city" },
                { text: "Postcode", value: "postcode" },
                { text: "Channel Type", value: "channelType" },
                { text: "Mids", value: "mids" }

            ],

            headersTable1: [
                { text: "Date", value: "timestamp" },
                { text: "Action", value: "action" },
                { text: "Notes", value: "notes" },
            ],

            supportAction: {
                actions: undefined,
                notes: ""
            },

            retailers: [

            ],
            orderByList: [
                { "id": "NO_ISSUE_IDENTIFIED", "text": "NO_ISSUE_IDENTIFIED" },
                { "id": "IDENTIFIED_MISSING_OFFER_DATA", "text": "IDENTIFIED_MISSING_OFFER_DATA" },
                { "id": "IDENTIFIED_TECHNICAL_ISSUE", "text": "IDENTIFIED_TECHNICAL_ISSUE" },
                { "id": "RE_RUN_TRANSACTION", "text": "RE_RUN_TRANSACTION" },
                { "id": "GENERATE_CASHBACK", "text": "GENERATE_CASHBACK" },
                { "id": "RESOLVED_QUERY", "text": "RESOLVED_QUERY" },
                { "id": "ESCALATED_TO_CASHBACK_TECHNOLOGY_PROVIDER", "text": "ESCALATED_TO_BOOGI" },
            ],
            offerType: [],
            orderBy: "MOSTPOPULAR",
        }
    },
    created() {
        this.getSupportTicket(this.ticketId)


    },
    methods: {

        downloadLogs() {
            const csvRows = []
            // Header
            csvRows.push(['Timestamp', 'Action', 'Notes'])
            // Rows
            this.actionLog.forEach(row => {
                csvRows.push([
                    row.timestamp,
                    row.action,
                    `"${row.notes.replace(/"/g, '""')}"`
                ])
            })

            // Convert to CSV string
            const csvContent = csvRows.map(e => e.join(',')).join('\n')

            // Trigger download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
            const link = document.createElement('a')
            link.href = URL.createObjectURL(blob)
            link.download = `action_logs_${this.missedTransactionQuery.id}.csv`
            link.click()
        },

        getCurrencySymbol(currencyCode, locale = 'en-US') {
            return (0).toLocaleString(locale, {
                style: 'currency',
                currency: currencyCode,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).replace(/\d/g, '').trim(); // strip digits, leave symbol
        },

        checkExpiry(dateObject) {
            const now = DateTime.now();
            const expiry = DateTime.fromMillis(dateObject)
            return now < expiry
        },

        niceDateTime(dateObject) {
            return DateTime.fromMillis(dateObject).toLocaleString(DateTime.DATETIME_MED)
        },

        async addSupportTicketAction() {

            try {
                const token = localStorage.getItem('token')
                const api = localStorage.getItem('apiType')

                this.loading = true
                const response = await this.$apiFetch(`https://${api}.boogi.io/private/api/client/support/${this.ticketId}`, {
                    method: "POST",
                    body: JSON.stringify(this.supportAction),
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `${token}`,
                    },
                });

                this.supportAction = {
                    actions: undefined,
                    notes: ""
                }


                if (response.ok) {
                    var object = await response.json()


                    this.missedTransactionQuery = object.missedTransactionQuery
                    this.transactionData = [object.transactionData]
                    this.cashbackObjectList = object.cashbackObjectList
                    this.endUser = object.endUser
                    this.endUserActivations = object.endUserOfferActivations
                    this.expectedOffer = object.missedTransactionQuery.programOfferRequested
                    this.actionLog = Object.entries(object.missedTransactionQuery.actionLog).map(([timestamp, data]) => ({
                        timestamp: DateTime.fromISO(timestamp).toFormat('yyyy-LL-dd HH:mm:ss'), // or keep raw ISO if preferred
                        action: data.actions,
                        notes: data.notes
                    }))


                    this.loading = false
                }

            } catch (error) {
                return;
            }

        },


        async getSupportTicket(id) {
            try {
                const token = localStorage.getItem('token')
                const api = localStorage.getItem('apiType')

                this.loading = true
                const response = await this.$apiFetch(`https://${api}.boogi.io/private/api/client/support/${id}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `${token}`,
                    },
                });

                if (response.ok) {
                    var object = await response.json()

                    this.missedTransactionQuery = object.missedTransactionQuery
                    this.transactionData = [object.transactionData]
                    this.cashbackObjectList = object.cashbackObjectList
                    this.endUser = object.endUser
                    this.endUserActivations = object.endUserOfferActivations
                    this.expectedOffer = object.missedTransactionQuery.programOfferRequested
                    this.actionLog = Object.entries(object.missedTransactionQuery.actionLog).map(([timestamp, data]) => ({
                        timestamp: DateTime.fromISO(timestamp).toFormat('yyyy-LL-dd HH:mm:ss'), // or keep raw ISO if preferred
                        action: data.actions,
                        notes: data.notes
                    }))


                    this.loading = false
                }

            } catch (error) {
                return;
            }

        },

    },
    computed: {
        listOfTiers() {
            return this.endUser.customerTiers.map((item, index) => ({
                id: index + 1,
                name: item,
            }))
        },
        listOfAssignedOffers() {
            return this.endUser.assignedProgramOffers.map((item, index) => ({
                id: index + 1,
                name: item,
            }))
        }

    },
    mounted() {
        document.title = "Dashboard | Support tickets";
    },
    watch: {
        serverOptions: {
            deep: true,
            handler(val) {
                this.getSupportTickets(val.page - 1, val.rowsPerPage)
            }
        },
        activateOfferRequest: {
            deep: true,
            handler: function (newValue) {

                if (newValue.splitOfCashbackPercentage.endUser == "") {
                    this.activateOfferRequest.splitOfCashbackPercentage.endUser = 0

                }

                if (newValue.splitOfCashbackPercentage.endUser > 100) {
                    this.activateOfferRequest.splitOfCashbackPercentage.endUser = 100
                    this.pecentageNotRight = "The percentage needs to be less than or equal to 100"
                }
                else if (newValue.splitOfCashbackPercentage.endUser < 0) {
                    this.activateOfferRequest.splitOfCashbackPercentage.endUser = 0
                    this.pecentageNotRight = "The percentage needs to be greater than or equal to 0"

                }
                this.pecentageNotRight = ""
                this.activateOfferRequest.splitOfCashbackPercentage.client = 100 - newValue.splitOfCashbackPercentage.endUser
            },
        }
    }
}
</script>